import React from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Skeletons, EmptyState } from "@/components/common/EmptyState";
import { useResource } from "@/hooks/useResource";
import { Button } from "@/components/ui/button";
import { SealCheck, Check, X, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Approvals() {
  const { items, isLoading, update } = useResource("approvals");

  const act = (id, status) => {
    update.mutate({ id, body: { status } });
    toast.success(`Marked as ${status}`);
  };

  const pending = items.filter((a) => a.status === "Pending");
  const done = items.filter((a) => a.status !== "Pending");

  return (
    <div>
      <PageHeader title="Approvals" subtitle="Review, approve and keep the audit trail clean" icon={SealCheck} />
      {isLoading ? <Skeletons /> : items.length === 0 ? <EmptyState /> : (
        <div className="space-y-6">
          <div>
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">Awaiting you ({pending.length})</h2>
            <div className="space-y-3">
              {pending.map((a) => (
                <div key={a.id} data-testid="approval-item" className="flex flex-col gap-3 rounded-2xl border border-border bg-surface-raised p-5 sm:flex-row sm:items-center">
                  <div className="flex-1">
                    <div className="flex items-center gap-2"><p className="font-display font-bold">{a.title}</p><StatusBadge status={a.type} /></div>
                    <p className="mt-1 text-sm text-muted-foreground">Submitted by {a.submitter} · {new Date(a.submitted_at).toLocaleDateString()} {a.amount ? `· ₹${(a.amount / 1000).toFixed(0)}K` : ""}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => act(a.id, "Changes requested")} variant="outline" size="sm" className="rounded-full" data-testid="approval-changes"><ArrowsClockwise size={14} className="mr-1" /> Changes</Button>
                    <Button onClick={() => act(a.id, "Rejected")} variant="outline" size="sm" className="rounded-full text-brand-pink" data-testid="approval-reject"><X size={14} className="mr-1" /> Reject</Button>
                    <Button onClick={() => act(a.id, "Approved")} size="sm" className="rounded-full bg-lime-500 text-black hover:bg-lime-400" data-testid="approval-approve"><Check size={14} weight="bold" className="mr-1" /> Approve</Button>
                  </div>
                </div>
              ))}
              {pending.length === 0 && <EmptyState title="All caught up!" subtitle="No approvals waiting on you." />}
            </div>
          </div>

          <div>
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wider text-muted-foreground">History</h2>
            <div className="overflow-x-auto rounded-2xl border border-border bg-surface-raised">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left">{["Item", "Type", "Submitter", "Reviewer", "Status"].map((h) => <th key={h} className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{h}</th>)}</tr></thead>
                <tbody>
                  {done.map((a) => (
                    <tr key={a.id} className="border-b border-border/60"><td className="px-4 py-3 font-medium">{a.title}</td><td className="px-4 py-3">{a.type}</td><td className="px-4 py-3">{a.submitter}</td><td className="px-4 py-3">{a.reviewer}</td><td className="px-4 py-3"><StatusBadge status={a.status} /></td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
